// Ícone e tom dos KPIs saem de `VOCABULARIO_KPI` (`src/auditoria/components/ds`).

import type { SxProps } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';

import useSWR from 'swr';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import Box from '@mui/material/Box';

import { fetcher } from 'src/lib/axios';
import { DashboardContent } from 'src/layouts/dashboard/content';
import { TxDataGrid } from 'src/auditoria/components/tx-data-grid';
import {
  DsNum,
  DsButton,
  DsKpiCard,
  GradePager,
  GradeCanvas,
  DsPageHeader,
  DsEmptyState,
  DsStatusChip,
  DS_HEAD_HEIGHT,
  SX_GRADE_CANVAS,
} from 'src/auditoria/components/ds';

import { formatDateBr } from '../Nfse/types';

// ----------------------------------------------------------------------
// Meio Ambiente → Licenciamento (portal SN). Licenças ambientais do contribuinte.
// Backend: GET /portal-api/meio-ambiente (meioambiente.licenciamentoambientealset,
// filtrado por fkempresa = persona). Ver SPEC-PORTAL-SN-TOMADOR-PF.md §8.
//
// Canvas: `portal-meio-ambiente.dc.html` (com licenças) e
// `portal-meio-ambiente-estado-vazio.dc.html` (sem). São a MESMA tela: o vazio
// mantém cabeçalho, CTA, os 4 KPIs zerados e o `.thead` acima do bloco vazio —
// por isso o `EmptyState` de página inteira saiu e entrou o `DsEmptyState`
// dentro do card da grade (§4.5).
// ----------------------------------------------------------------------

// O `TxDataGrid` do portal declara `sx?: SxProps` (tema default do MUI) enquanto o
// kit tipa `SxProps<Theme>` do tema do app. Mesma forma, tipos nominalmente
// distintos: o estreitamento acontece uma vez aqui, sem `any` e sem `@ts-ignore`.
const { sx: SX_GRADE_KIT, ...PROPS_GRADE } = SX_GRADE_CANVAS;
const SX_GRADE = SX_GRADE_KIT as SxProps;

interface Licenca {
  id: number;
  numero: string | null;
  processo: string | null;
  situacao: number | null;
  data_emissao: string | null;
  validade: string | null;
  tipo_licenca: string | null;
  atividade: string | null;
}

interface MeioAmbienteResponse {
  licencas: Licenca[];
}

const TAMANHO_PAGINA = 10;
const DIAS_ALERTA = 90;

/** Colunas do artboard, também usadas para desenhar o `.thead` do estado vazio. */
const COLUNAS_CANVAS = [
  { campo: 'numero', titulo: 'Número', largura: '130px' },
  { campo: 'tipo', titulo: 'Tipo', largura: '1fr' },
  { campo: 'validade', titulo: 'Validade', largura: '120px' },
  { campo: 'situacao', titulo: 'Situação', largura: '130px' },
  { campo: 'acoes', titulo: '', largura: '120px' },
] as const;

function hoje(): number {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Dias até a validade; `null` quando a licença ainda não tem validade (em análise). */
function diasAteValidade(validade: string | null): number | null {
  if (!validade) return null;
  const t = new Date(validade).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.round((t - hoje()) / 86_400_000);
}

/**
 * Situação exibida. O `situacao` do legado é um enum numérico sem dicionário
 * publicado (a view anterior imprimia "Situação 3"), então o vocabulário do
 * canvas — Vigente · Em análise · Vencida — é derivado da validade, que é o
 * campo que o contribuinte de fato lê. Registrado no mailbox.
 */
function situacaoLicenca(licenca: Licenca): {
  rotulo: string;
  tom: 'success' | 'warning' | 'error';
} {
  const dias = diasAteValidade(licenca.validade);
  if (dias === null) return { rotulo: 'Em análise', tom: 'warning' };
  if (dias < 0) return { rotulo: 'Vencida', tom: 'error' };
  if (dias <= DIAS_ALERTA) return { rotulo: 'A vencer', tom: 'warning' };
  return { rotulo: 'Vigente', tom: 'success' };
}

/** `.thead` do canvas sem grade por baixo — só existe no par vazio do artboard. */
function CabecalhoVazio() {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: COLUNAS_CANVAS.map((c) => c.largura).join(' '),
        gap: '10px',
        alignItems: 'center',
        height: DS_HEAD_HEIGHT,
        padding: '0 14px',
        fontSize: 14,
        fontWeight: 600,
        color: 'text.secondary',
        backgroundColor: 'background.neutral',
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {COLUNAS_CANVAS.map((coluna) => (
        <Box component="span" key={coluna.campo}>
          {coluna.titulo}
        </Box>
      ))}
    </Box>
  );
}

export default function MeioAmbienteListPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useSWR<MeioAmbienteResponse>('/portal-api/meio-ambiente', fetcher);
  const [pagina, setPagina] = useState(0);

  const licencas = data?.licencas ?? [];
  const vazio = !isLoading && licencas.length === 0;

  const vigentes = licencas.filter((l) => {
    const dias = diasAteValidade(l.validade);
    return dias !== null && dias >= 0;
  });
  const vencendo = vigentes.filter((l) => (diasAteValidade(l.validade) ?? 0) <= DIAS_ALERTA);
  const emAnalise = licencas.filter((l) => diasAteValidade(l.validade) === null);

  const paginaLicencas = licencas.slice(pagina * TAMANHO_PAGINA, (pagina + 1) * TAMANHO_PAGINA);
  const temPaginacao = licencas.length > TAMANHO_PAGINA;

  const solicitarLicenca = () => navigate('/protocolo/novo');

  const columns: GridColDef<Licenca>[] = [
    {
      field: 'numero',
      headerName: 'Número',
      width: 130,
      renderCell: (params) => <DsNum mono>{params.row.numero ?? '—'}</DsNum>,
    },
    {
      field: 'tipo',
      headerName: 'Tipo',
      flex: 1,
      minWidth: 240,
      valueGetter: (_v, row) =>
        [row.tipo_licenca, row.atividade].filter(Boolean).join(' · ') || '—',
    },
    {
      field: 'validade',
      headerName: 'Validade',
      width: 120,
      renderCell: (params) => <DsNum mono>{formatDateBr(params.row.validade)}</DsNum>,
    },
    {
      field: 'situacao',
      headerName: 'Situação',
      width: 130,
      sortable: false,
      renderCell: (params) => {
        const { rotulo, tom } = situacaoLicenca(params.row);
        return <DsStatusChip tone={tom}>{rotulo}</DsStatusChip>;
      },
    },
  ];

  return (
    <DashboardContent>
      <DsPageHeader
        weight={800}
        title="Meio Ambiente"
        subtitle="Licenciamento ambiental do estabelecimento · validades e renovações."
        action={
          <DsButton icon="mais" size="tall" onClick={solicitarLicenca}>
            Solicitar licença
          </DsButton>
        }
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            md: 'repeat(4, 1fr)',
          },
          gap: '12px',
          mb: '12px',
        }}
      >
        <DsKpiCard
          scale="sans28"
          order="title-first"
          title="Licenças vigentes"
          total={<DsNum>{vigentes.length}</DsNum>}
          sub="com validade em dia"
          conceito="documento-regular"
          borderLeft
        />
        <DsKpiCard
          scale="sans28"
          order="title-first"
          title="Vencendo em 90 dias"
          total={<DsNum>{vencendo.length}</DsNum>}
          sub="prazo de renovação aberto"
          conceito="vencimento-proximo"
          borderLeft
        />
        <DsKpiCard
          scale="sans28"
          order="title-first"
          title="Em análise"
          total={<DsNum>{emAnalise.length}</DsNum>}
          sub="processos sem validade emitida"
          conceito="aguardando"
          borderLeft
        />
        <DsKpiCard
          scale="sans28"
          order="title-first"
          title="Autos lavrados"
          total={<DsNum>—</DsNum>}
          sub="não publicado neste portal"
          conceito="ocorrencia-adversa"
          borderLeft
        />
      </Box>

      <GradeCanvas
        pager={
          temPaginacao && !vazio ? (
            <GradePager
              de={pagina * TAMANHO_PAGINA + 1}
              ate={Math.min((pagina + 1) * TAMANHO_PAGINA, licencas.length)}
              total={licencas.length}
              onPrev={() => setPagina((p) => Math.max(0, p - 1))}
              onNext={() => setPagina((p) => p + 1)}
              prevDisabled={pagina === 0}
              nextDisabled={(pagina + 1) * TAMANHO_PAGINA >= licencas.length}
            />
          ) : undefined
        }
      >
        {vazio ? (
          <>
            <CabecalhoVazio />
            <DsEmptyState
              icon="info"
              title="Nenhum licenciamento ambiental"
              description="Este estabelecimento ainda não possui licenças LP, LI ou LO. Solicite a primeira licença para acompanhar validade e situação por aqui."
              action={
                <DsButton icon="mais" size="tall" onClick={solicitarLicenca}>
                  Solicitar licença
                </DsButton>
              }
            />
          </>
        ) : (
          <TxDataGrid<Licenca>
            rows={paginaLicencas}
            columns={columns}
            loading={isLoading}
            emptyLabel="Nenhum licenciamento ambiental"
            initialPageSize={TAMANHO_PAGINA}
            hideFooter
            {...PROPS_GRADE}
            sx={SX_GRADE}
          />
        )}
      </GradeCanvas>
    </DashboardContent>
  );
}
